import React, { useEffect, useState } from "react";
import axios from "axios";
import { crearAsignacion, obtenerAsignacionesPorClase } from "./constants";
import { useData } from "./DataContext";
import NavigationIcon from "../Others/NavigationIcon";
import "./HomeworkModule.css";

const formatDate = (value) => {
  if (!value) return "Sin fecha límite";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const AsignarTareas = () => {
  const { dataClase } = useData();
  const [nombre, setNombre] = useState("");
  const [instrucciones, setInstrucciones] = useState("");
  const [fechaLimite, setFechaLimite] = useState("");
  const [tareas, setTareas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ type: "", text: "" });

  const obtenerTareasAsignadas = async () => {
    if (!dataClase?.matricula_clase) {
      setStatus({
        type: "error",
        text: "Selecciona una clase para consultar sus tareas.",
      });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const url = obtenerAsignacionesPorClase(dataClase.matricula_clase);
      const response = await axios.get(url);
      setTareas(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error al obtener las tareas asignadas:", error);
      setTareas([]);
      setStatus({
        type: "error",
        text: "No fue posible cargar las tareas asignadas.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    obtenerTareasAsignadas();
  }, [dataClase?.matricula_clase]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!nombre || !instrucciones || !fechaLimite) {
      setStatus({
        type: "error",
        text: "Completa todos los campos antes de asignar la tarea.",
      });
      return;
    }

    try {
      setSaving(true);
      setStatus({ type: "", text: "" });
      await axios.post(crearAsignacion, {
        nombre,
        instrucciones,
        matricula_clase: dataClase.matricula_clase,
        fecha_limite: fechaLimite,
        calificacion_maxima: 10,
        estado: "abierta",
        tipo_asignacion: "tarea",
        archivo_referencia: null,
      });

      setNombre("");
      setInstrucciones("");
      setFechaLimite("");
      setStatus({
        type: "success",
        text: "La tarea se asignó correctamente.",
      });
      await obtenerTareasAsignadas();
    } catch (error) {
      console.error("Error al asignar la tarea:", error);
      setStatus({
        type: "error",
        text: "No fue posible asignar la tarea. Intenta nuevamente.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <section className="homework-section" aria-labelledby="assign-homework-title">
        <header className="homework-section-header">
          <div>
            <h2 id="assign-homework-title">Asignar nueva tarea</h2>
            <p>Define las indicaciones y la fecha límite para el grupo.</p>
          </div>
        </header>

        {status.text && (
          <div
            className={"homework-status is-" + status.type}
            role={status.type === "error" ? "alert" : "status"}
          >
            {status.text}
          </div>
        )}

        <form className="homework-form" onSubmit={handleSubmit}>
          <div className="homework-form-grid">
            <div className="homework-field">
              <label htmlFor="homework-name">Nombre de la tarea</label>
              <input
                id="homework-name"
                type="text"
                placeholder="Ejemplo: Ensayo sobre Derechos Humanos"
                value={nombre}
                onChange={(event) => setNombre(event.target.value)}
              />
            </div>

            <div className="homework-field">
              <label htmlFor="homework-date">Fecha límite</label>
              <input
                id="homework-date"
                type="datetime-local"
                value={fechaLimite}
                onChange={(event) => setFechaLimite(event.target.value)}
              />
            </div>

            <div className="homework-field is-wide">
              <label htmlFor="homework-instructions">Instrucciones</label>
              <textarea
                id="homework-instructions"
                placeholder="Describe con claridad lo que debe entregar el alumno."
                value={instrucciones}
                onChange={(event) => setInstrucciones(event.target.value)}
              />
            </div>
          </div>

          <div className="homework-form-actions">
            <button
              type="button"
              className="homework-button-secondary"
              disabled={loading}
              onClick={obtenerTareasAsignadas}
            >
              <NavigationIcon name="content" size={18} />
              {loading ? "Actualizando..." : "Actualizar lista"}
            </button>
            <button type="submit" className="homework-button" disabled={saving}>
              <NavigationIcon name="tasks" size={18} />
              {saving ? "Asignando..." : "Asignar tarea"}
            </button>
          </div>
        </form>
      </section>

      <section className="homework-section" aria-labelledby="assigned-homework-title">
        <header className="homework-section-header">
          <div>
            <h2 id="assigned-homework-title">Tareas asignadas</h2>
            <p>Consulta las actividades vigentes de esta clase.</p>
          </div>
          <span className="homework-badge">
            {tareas.length} {tareas.length === 1 ? "tarea" : "tareas"}
          </span>
        </header>

        {loading ? (
          <div className="homework-loading" aria-live="polite">
            <span className="homework-spinner" />
            <strong>Cargando tareas</strong>
            <p>Estamos actualizando la lista de actividades asignadas.</p>
          </div>
        ) : tareas.length === 0 ? (
          <div className="homework-empty">
            <span className="homework-empty-icon">
              <NavigationIcon name="tasks" size={28} />
            </span>
            <strong>Aún no hay tareas asignadas</strong>
            <p>Utiliza el formulario superior para crear la primera actividad.</p>
          </div>
        ) : (
          <div className="homework-list">
            {tareas.map((tarea, index) => (
              <article
                className="homework-card"
                key={tarea.id_asignacion || index}
              >
                <span className="homework-icon">
                  <NavigationIcon name="tasks" size={25} />
                </span>
                <div className="homework-card-main">
                  <span className="homework-kicker">Actividad asignada</span>
                  <h3>{tarea.nombre}</h3>
                  <p>{tarea.instrucciones}</p>
                  <div className="homework-card-meta">
                    <span>Fecha límite: {formatDate(tarea.fecha_limite)}</span>
                    <span className="homework-badge">
                      Calificación máxima: {tarea.calificacion_maxima || 10}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </>
  );
};

export default AsignarTareas;
